import {TOKEN_GLINT} from '../configs/tokenGlintAnimation';
import {
  buildGlintMaskPoints,
  resolveFirstGlintDelayMs,
  resolveGlintBand,
  resolveGlintBandX,
  resolveGlintIntervalMs,
} from '../utils/effects/buildTokenGlint';

// Событие сцены Phaser «кадр»: приходит со временем кадра
const FRAME_EVENT = 'update';

const checkIfTargetDrawable = (target) => (
  Boolean(target?.hexKey)
  && Boolean(target.style?.texture?.key)
  && Number.isFinite(target.x) && Number.isFinite(target.y) && Number.isFinite(target.depth)
  && target.width > 0 && target.height > 0
);

/**
 * БЛИКИ НА ЖЕТОНАХ: по жетонам ключевых точек время от времени пробегает
 * свет — резкий блеск по металлическим (великие руины и рудник) и мягкий
 * световой блик по каменным (руины). Вид блика у материала свой (см.
 * configs/tokenGlintAnimation), а устроены они одинаково.
 *
 * У КАЖДОГО ЖЕТОНА СВОЙ СЧЕТ ВРЕМЕНИ, и хранится он здесь по ключу ячейки, а
 * не в спрайте: поле перерисовывается на каждое обновление стора и спрайты
 * жетонов создаются заново — счет, привязанный к спрайту, сбрасывался бы с
 * каждым ходом, и жетон не блеснул бы никогда. Рендерер после перерисовки
 * лишь сообщает, где сейчас стоят такие жетоны (sync).
 *
 * ВРЕМЯ ИДЕТ ПО КАДРАМ, а не по настенным часам: в скрытой вкладке блеск
 * стоит и после возврата продолжает с того же места. По часам все жетоны,
 * «опоздавшие» за время отсутствия, блеснули бы разом.
 *
 * Блик — наклонная полоса света, обрезанная по контуру жетона и сложенная с
 * ним светом. Спрайты блика принадлежат сцене, а не ячейке: перерисовка поля
 * их не трогает, и блик добегает до конца.
 *
 * Блеск — украшение: его сбой не должен ронять кадр или перерисовку поля,
 * поэтому после первой же ошибки он до конца сцены отключается.
 */
export default class TokenGlints {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} [options]
   * @param {function(): number} [options.rng] — источник случайности (в тестах — заглушка)
   * @param {function(string): boolean} [options.checkIfTokenVisible] — виден ли
   *   сейчас жетон ячейки: пока на ячейку летит двойник жетона, сам жетон
   *   спрятан, и блестеть нечему
   */
  constructor(scene, {rng = Math.random, checkIfTokenVisible = () => true} = {}) {
    this.scene = scene;
    this.rng = rng;
    this.checkIfTokenVisible = checkIfTokenVisible;
    // Жетоны поля, по которым бегают блики: ключ ячейки -> место жетона, вид
    // его блика и время до следующего
    this.tokens = new Map();
    // Бегущие блики: ключ ячейки -> спрайт полосы, маска и пройденное время
    this.active = new Map();
    this.isListening = false;
    this.isFailed = false;
  }

  /**
   * Поле перерисовано: вот где сейчас стоят жетоны с бликами. Новым жетонам
   * заводится счет времени, у прежних он продолжается, исчезнувшие забываются
   * вместе со своим бликом
   * @param {Array<{hexKey: string, x: number, y: number, width: number, height: number, depth: number, style: Object}>} targets —
   *   центр, размер на поле и слой спрайта жетона; style — вид блика его
   *   материала (TOKEN_GLINT_STYLES)
   */
  sync(targets = []) {
    this.guard(() => {
      const nextKeys = new Set();

      targets.filter(checkIfTargetDrawable).forEach(({hexKey, x, y, width, height, depth, style}) => {
        nextKeys.add(hexKey);
        const token = this.tokens.get(hexKey);
        if (token && token.style === style) {
          Object.assign(token, {x, y, width, height, depth});
          return;
        }
        // Новый жетон — либо на ячейке теперь жетон другого материала: прежний
        // блик ему не подходит, счет времени заводится заново
        this.stop(hexKey);
        this.tokens.set(hexKey, {
          x, y, width, height, depth, style,
          remainingMs: resolveFirstGlintDelayMs(this.rng, style),
        });
      });

      [...this.tokens.keys()].filter((hexKey) => !nextKeys.has(hexKey)).forEach((hexKey) => {
        this.stop(hexKey);
        this.tokens.delete(hexKey);
      });

      this.updateListening();
    });
  }

  /**
   * Шаг времени: бегущие блики двигаются, у жетонов убывает время до проблеска
   * @param {number} delta — время кадра, мс
   */
  update(delta) {
    this.guard(() => {
      const stepMs = Math.min(delta, TOKEN_GLINT.maxStepMs);
      if (!(stepMs > 0)) {
        return;
      }

      this.active.forEach((glint, hexKey) => {
        glint.elapsedMs += stepMs;
        if (glint.elapsedMs >= glint.durationMs) {
          this.stop(hexKey);
        } else {
          glint.sprite.setPosition(resolveGlintBandX(glint.band, glint.elapsedMs / glint.durationMs), glint.band.y);
        }
      });

      this.tokens.forEach((token, hexKey) => {
        token.remainingMs -= stepMs;
        if (token.remainingMs > 0) {
          return;
        }
        // Следующий промежуток отсчитывается в любом случае: жетон, которому
        // сейчас блестеть нельзя (спрятан под летящим двойником), этот раз
        // просто пропускает
        token.remainingMs = resolveGlintIntervalMs(this.rng, token.style);
        this.play(hexKey);
      });

      this.updateListening();
    });
  }

  /**
   * Пустить блик по жетону ячейки прямо сейчас
   * @param {string} hexKey
   * @returns {boolean} побежал ли блик
   */
  play(hexKey) {
    const token = this.tokens.get(hexKey);
    if (this.isFailed || !token || this.active.has(hexKey) || !this.checkIfTokenVisible(hexKey)) {
      return false;
    }
    const {style} = token;
    if (!this.ensureTexture(style.texture)) {
      return false;
    }

    // Без маски полоса легла бы и на соседние ячейки — тогда блика нет вовсе
    const maskShape = this.scene.make?.graphics?.({add: false});
    if (!maskShape?.fillPoints || !maskShape.createGeometryMask) {
      maskShape?.destroy?.();
      return false;
    }

    const glint = {
      sprite: null, maskShape, band: resolveGlintBand(token, style), durationMs: style.durationMs, elapsedMs: 0,
    };
    // Запись заводится до сборки спрайта: сорвись сборка на середине, stop
    // уберет все, что успело появиться
    this.active.set(hexKey, glint);
    try {
      maskShape.fillStyle(0xFFFFFF, 1);
      maskShape.fillPoints(buildGlintMaskPoints(token), true);

      const sprite = this.scene.add.image(glint.band.startX, glint.band.y, style.texture.key);
      glint.sprite = sprite;
      sprite.setOrigin(0.5, 0.5);
      sprite.setDisplaySize(glint.band.bandWidth, glint.band.bandHeight);
      sprite.setAngle(glint.band.angleDeg);
      sprite.setDepth(token.depth + TOKEN_GLINT.depthOffset);
      // Свет складывается с жетоном, а не закрашивает его
      sprite.setBlendMode('ADD');
      sprite.setTint(style.tint);
      sprite.setAlpha(style.alpha);
      sprite.setMask(maskShape.createGeometryMask());
    } catch (error) {
      this.stop(hexKey);
      throw error;
    }

    this.updateListening();
    return true;
  }

  /** Убрать бегущий блик ячейки, не дожидаясь конца */
  stop(hexKey) {
    const glint = this.active.get(hexKey);
    if (!glint) {
      return;
    }
    this.active.delete(hexKey);
    glint.sprite?.clearMask?.(false);
    glint.sprite?.destroy?.();
    glint.maskShape?.destroy?.();
  }

  /** Забыть все жетоны и убрать все блики: поле разбирается */
  clear() {
    [...this.active.keys()].forEach((hexKey) => {
      try {
        this.stop(hexKey);
      } catch (error) {
        // Сцена уже разбирается: спрайт мог быть уничтожен раньше нас
      }
    });
    this.active.clear();
    this.tokens.clear();
    this.updateListening();
  }

  destroy() {
    this.clear();
  }

  /**
   * Картинка блика — белая полоса с мягкими краями, у каждого материала своя, —
   * рисуется в память один раз на сцену
   * @param {{key: string, width: number, height: number, stops: Array}} texture
   * @returns {boolean} есть ли картинка
   */
  ensureTexture({key, width, height, stops}) {
    const textures = this.scene.textures;
    if (textures?.exists?.(key)) {
      return true;
    }

    const canvasTexture = textures?.createCanvas?.(key, width, height);
    const context = canvasTexture?.context;
    if (!context?.createLinearGradient) {
      // Рисовать нечем: пустую картинку не оставляем, иначе блик был бы
      // невидимым, но считался бы показанным
      if (canvasTexture) {
        textures.remove?.(key);
      }
      return false;
    }

    const gradient = context.createLinearGradient(0, 0, width, 0);
    stops.forEach(([offset, alpha]) => gradient.addColorStop(offset, `rgba(255, 255, 255, ${alpha})`));
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    canvasTexture.refresh();
    return true;
  }

  /** Кадры слушаем, только пока есть кому блестеть */
  updateListening() {
    const events = this.scene.events;
    const shouldListen = !this.isFailed && (this.tokens.size > 0 || this.active.size > 0);

    if (shouldListen && !this.isListening && events?.on) {
      events.on(FRAME_EVENT, this.handleFrame, this);
      this.isListening = true;
    } else if (!shouldListen && this.isListening) {
      events?.off?.(FRAME_EVENT, this.handleFrame, this);
      this.isListening = false;
    }
  }

  handleFrame(time, delta) {
    this.update(delta);
  }

  /** Сбой блеска не должен уронить кадр или перерисовку поля — блеск отключается */
  guard(action) {
    if (this.isFailed) {
      return;
    }
    try {
      action();
    } catch (error) {
      console.warn('[TokenGlints] Блеск жетонов отключен: сбой', error);
      this.isFailed = true;
      this.clear();
    }
  }
}
